/**
 * DQL (Ditto Query Language) types for HTTP API responses
 *
 * These types define the structure of responses from DQL query execution
 * via the HTTP API.
 */

/**
 * The ID of a document
 */
export type DocumentID = unknown

/**
 * The type of DQL query that was executed
 */
export type DQLQueryType =
  | 'select'
  | 'mutation' // for insert / update / tombstone
  | 'explain'
  | 'unknown' // when returning an error before the query is known

type EmptyObject = Record<string, never>

/**
 * An error emitted present in the DQLHTTPResponse. Will be present in the `error` field
 * of the response
 */
export type DQLError =
  | EmptyObject
  | {
      /**
       * The message from the error
       */
      description: string
    }

/**
 * A warning emitted present in the DQLHTTPResponse. Will be present in the `warnings` array
 */
export type DQLWarning = {
  /**
   * If present, the document id at which the evaluation-time error happened
   */
  _id?: DocumentID
  /**
   * The message from the warning (evaluation-time errors count as warnings)
   */
  description: string
}

export type DQLResult = Record<string, unknown> & {
  /**
   * The id of the item
   */
  _id: DocumentID
}

/**
 * The response structure for a DQL query executed via the HTTP API. The HTTP status code will be:
 *
 * - 200 for any statement that got executed and completed successfully
 * - 4xx+ for any statement that did not pass a semantic check, or the execution completed with errors
 *
 * If the HTTP status code indicates a failure, the response object should still be in this structure
 */
export type DQLHTTPResponse = {
  /**
   * The transaction id. This might not be present for errors
   */
  transactionId?: number
  /**
   * The type of query that was executed. The value is the same as the `query-subtype` HTTP header
   */
  queryType: DQLQueryType
  /**
   * An array of materialized documents. Only populated for SELECT queries.
   *
   * NOTE: After V1, documents specified by the RETURNING clause will also be included in this array, e.g.
   * `INSERT INTO ... RETURNING ...`.
   */
  items: DQLResult[]
  mutatedDocumentIds?: DocumentID[]
  /**
   * A single error that prevented the query from executing / completing. This value
   * will always be present, even if there are no errors
   */
  error: DQLError
  /**
   * A list of warnings that happened during the query execution. A maximum of 10 warnings will be returned
   */
  warnings: DQLWarning[]
  /**
   * A total count of warnings that happened during the query execution. This value may be greater than the
   * number of warnings returned in the `warnings` array
   */
  totalWarningsCount?: number
}

/**
 * Type guard to check if a response is a valid DQLHTTPResponse
 */
export function isDQLHTTPResponse(
  response: unknown,
): response is DQLHTTPResponse {
  if (!response || typeof response !== 'object') {
    return false
  }

  const r = response as Record<string, unknown>

  // Check required fields
  if (
    !('queryType' in r) ||
    !('items' in r) ||
    !('error' in r) ||
    !('warnings' in r)
  ) {
    return false
  }

  // Validate field types
  const validQueryTypes = ['select', 'mutation', 'explain', 'unknown']
  if (
    typeof r.queryType !== 'string' ||
    !validQueryTypes.includes(r.queryType)
  ) {
    return false
  }

  if (!Array.isArray(r.items)) {
    return false
  }

  if (typeof r.error !== 'object' || r.error === null) {
    return false
  }

  if (!Array.isArray(r.warnings)) {
    return false
  }

  return true
}
